import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

/**
 * TypedSignaturePad
 * ---------------------------------------------------------------------------
 * "Type-to-sign" replacement for the freehand SignatureCanvas. Customer types
 * their full name → we render it in a cursive font into an offscreen canvas
 * → export as PNG data URL so the backend + everywhere-that-displays-signatures
 * keeps working unchanged.
 *
 * Why: freehand drawing on random phones/laptops was the #1 friction point
 * for customer sign-off (drawing with a mouse is unnatural, mobile canvases
 * misfire, older users get confused). Typing is legally equivalent under the
 * Australian Electronic Transactions Act 1999 when combined with the terms
 * checkbox + audit metadata (IP / UA / timestamp) captured on the server.
 *
 * @param {Function} onSave      Called with (dataUrl, { typed_name })
 * @param {Function} onCancel    Called on cancel (optional; hidden if not provided)
 * @param {string}   initialName Optional prefill (e.g. the customer's name on file)
 * @param {boolean}  disabled    External disable (e.g. terms not yet ticked)
 * @param {string}   disabledReason Text shown when disabled
 */
export const TypedSignaturePad = ({
  onSave,
  onCancel,
  initialName = '',
  disabled = false,
  disabledReason = '',
}) => {
  const [name, setName] = useState(initialName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fontReady, setFontReady] = useState(false);
  const canvasRef = useRef(null);

  // Load the cursive font before rendering. The Google Font 'Caveat' looks
  // handwritten and works on every device (or falls back gracefully to the
  // system cursive if the CDN is blocked).
  useEffect(() => {
    // Only inject once per page load
    if (!document.getElementById('typed-sig-caveat-font')) {
      const link = document.createElement('link');
      link.id = 'typed-sig-caveat-font';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Caveat:wght@600;700&display=swap';
      document.head.appendChild(link);
    }
    // document.fonts.load() returns a Promise once the font is usable in canvas
    if (document.fonts && document.fonts.load) {
      document.fonts.load('56px "Caveat"').then(() => setFontReady(true)).catch(() => setFontReady(true));
    } else {
      // Older browsers — give it a beat to arrive from the CDN
      const t = setTimeout(() => setFontReady(true), 400);
      return () => clearTimeout(t);
    }
  }, []);

  // Re-render the preview canvas whenever the name or fontReady changes.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    // Sharp on high-DPI screens
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    // Background: subtle underline like a signature line
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, h - 20);
    ctx.lineTo(w - 20, h - 20);
    ctx.stroke();

    // Name in cursive
    const label = (name || '').trim();
    if (label) {
      // Autosize down if the name is too long to fit
      let fontSize = 60;
      ctx.font = `600 ${fontSize}px "Caveat", "Brush Script MT", cursive`;
      while (ctx.measureText(label).width > w - 40 && fontSize > 24) {
        fontSize -= 2;
        ctx.font = `600 ${fontSize}px "Caveat", "Brush Script MT", cursive`;
      }
      ctx.fillStyle = '#1e293b';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(label, 30, h - 30);
    } else {
      // Placeholder hint
      ctx.fillStyle = '#cbd5e1';
      ctx.font = 'italic 18px system-ui, sans-serif';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText('Your signature will appear here…', 30, h - 30);
    }
  }, [name, fontReady]);

  const handleSubmit = async () => {
    if (disabled) {
      if (disabledReason) toast.error(disabledReason);
      return;
    }
    const trimmed = (name || '').trim();
    if (trimmed.length < 2) {
      toast.error('Please type your full name to sign');
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    setIsSubmitting(true);
    try {
      await onSave(dataUrl, { typed_name: trimmed });
      // Success — reset submitting flag in case parent doesn't unmount us
      // immediately (defensive: prevents any future stale-caller from hanging
      // the button on "Submitting…").
      setIsSubmitting(false);
    } catch (err) {
      // Parent handles the error toast; just re-enable the button
      setIsSubmitting(false);
    }
  };

  return (
    <div className="typed-signature-pad" data-testid="typed-signature-pad">
      <label
        htmlFor="typed-sig-name"
        style={{ display: 'block', fontWeight: 600, fontSize: '0.95rem', marginBottom: '0.4rem', color: '#1e293b' }}
      >
        Type your full name to sign
      </label>
      <input
        id="typed-sig-name"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Jane Doe"
        autoComplete="name"
        disabled={isSubmitting}
        data-testid="typed-signature-name-input"
        style={{
          width: '100%',
          padding: '0.75rem 1rem',
          fontSize: '1.1rem',
          border: '2px solid #cbd5e1',
          borderRadius: '0.5rem',
          outline: 'none',
          backgroundColor: '#ffffff',
        }}
      />

      <div style={{ marginTop: '1rem' }}>
        <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.25rem' }}>Preview:</div>
        <div
          style={{
            border: '2px dashed #e2e8f0',
            borderRadius: '0.5rem',
            backgroundColor: '#fafafa',
            padding: '0.5rem',
          }}
        >
          <canvas
            ref={canvasRef}
            style={{ width: '100%', height: '120px', display: 'block' }}
            data-testid="typed-signature-preview"
          />
        </div>
        <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.5rem', margin: '0.5rem 0 0' }}>
          By typing your name and clicking &quot;Accept &amp; Sign&quot;, you are electronically signing this job card.
          This is legally equivalent to a handwritten signature under the Electronic Transactions Act 1999 (Cth).
        </p>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', flexWrap: 'wrap' }}>
        {onCancel && (
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting} data-testid="typed-signature-cancel-btn">
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || disabled || (name || '').trim().length < 2}
          data-testid="typed-signature-submit-btn"
          style={{
            minWidth: '200px',
            fontSize: '1rem',
            padding: '0.75rem 1.5rem',
            fontWeight: 600,
          }}
        >
          {isSubmitting ? 'Submitting…' : '✓ Accept & Sign'}
        </Button>
      </div>

      {disabled && disabledReason && (
        <p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: '0.75rem', fontWeight: 500 }}>
          ⚠️ {disabledReason}
        </p>
      )}
    </div>
  );
};

export default TypedSignaturePad;
